class API {
  constructor (serialWrapper, lock) {
    this.serialWrapper = serialWrapper
    this.lock = lock
  }

  async getStatus () {
    await this.lock.acquire()

    try {
      return await this.serialWrapper.runCommand(74, 'W')
    } finally {
      this.lock.release()
    }
  }

  async checkIfHasUnfinishedFiscalOperationAndAbortIt (maxRetries = 5, attempt = 1) {
    if (attempt > maxRetries) {
      throw new Error('Max retries reached. Unable to close the fiscal receipt.')
    }

    const res = await this.serialWrapper.runCommand(76)
    const statusData = res.data.split(',')

    if (parseInt(statusData[0]) > 0) {
      if (statusData[0] === '1' || statusData[0] === '5') {
        console.log('Fiscal receipt is open. Trying to close it and unblock...')

        await this.serialWrapper.runCommand(53, '\t')
        await this.serialWrapper.runCommand(56) // close fiscal receipt?

        return this.checkIfHasUnfinishedFiscalOperationAndAbortIt(maxRetries, attempt + 1)
      }

      if (['2', '3', '4'].indexOf(statusData[0]) !== -1) {
        console.log('Refund receipt is open. Trying to close it and unblock...')

        await this.serialWrapper.runCommand(53, '\t')
        await this.serialWrapper.runCommand(56) // close fiscal receipt DAVID
        await this.serialWrapper.runCommand(86) // close void receipt ACCENT

        return this.checkIfHasUnfinishedFiscalOperationAndAbortIt(maxRetries, attempt + 1)
      }

      throw new Error(`Unknown open status code: ${statusData[0]}`)
    }

    return 'No unfinished fiscal operations detected.'
  }

  async createSale (order = {}) {
    const defaultOptions = {
      operatorCode: '1',
      operatorPass: '0000',
      tillNmb: '1',
      lines: [],
      payments: []
    }

    Object.assign(defaultOptions, this.filterUndefinedValues(order))
    const fiscalReceiptCommands = []
    if (defaultOptions.isRefund && !defaultOptions.isDavid) {
      // 55h(48) OPENING A VOID CLIENT'S RECEIPT
      fiscalReceiptCommands.push(
        [85, `${defaultOptions.operatorCode},${defaultOptions.operatorPass},${defaultOptions.tillNmb}`]
      )
    } else {
      // 30h(48) OPENING A FISCAL CLIENT'S RECEIPT
      fiscalReceiptCommands.push(
        [48, `${defaultOptions.operatorCode},${defaultOptions.operatorPass},${defaultOptions.tillNmb}${(defaultOptions.isRefund && defaultOptions.isDavid) ? ',R' : ''}`]
      )
    }

    const sanitize = (str) => str.replace(/[;,/.']|[ ]{2}/g, '')
    // Set order lines
    defaultOptions.lines.forEach((line) => {
      let lineData = ''

      if (line.itemNo) {
        const sanitizedLine1 = sanitize(line.itemNo).substring(0, 25)
        lineData += `${sanitizedLine1}`
      }

      const productName = sanitize(line.itemName).substring(0, 25)

      lineData += line.itemNo ? `\n${productName}` : `${productName}`

      // Get VAT group bytes
      const taxGroup = this.getVatGroupBytes(line.vatGroup)

      // Check if the product is domestic
      const isDomestic = line.isDomestic ?? false

      // Format the price and tax group
      let priceData
      const domesticSign = defaultOptions.isDavid ? '' : '@'
      if (isDomestic) {
        // Domestic product: Add @ before the tax group
        priceData = `\t${domesticSign}${taxGroup}${line.itemPrice.toFixed(2)}*${line.quantity.toFixed(3)}`
      } else {
        // Non-domestic product: No @ sign
        priceData = `\t${taxGroup}${line.itemPrice.toFixed(2)}*${line.quantity.toFixed(3)}`
      }

      if (line.discount > 0) {
        if (defaultOptions.isDavid) {
          priceData += `$-${parseFloat(line.discount).toFixed(2)}`
        } else {
          priceData += `;-${parseFloat(line.discount).toFixed(2)}`
        }
      }

      lineData += priceData

      // 31h(49) REGISTRATION OF SALES
      fiscalReceiptCommands.push(
        [49, lineData]
      )
    })

    // Set payments
    const receiptNumber = order.receiptNumber ?? null
    let isReceiptNumberAdded = !receiptNumber

    Object.entries(defaultOptions.payments).forEach(([type, value]) => {
      // defaultOptions.payments.forEach((payment) => {
      if (value === 0) return

      let paymentData = ''

      const paymentType = this.getPaymentTypeString(type, defaultOptions.isDavid)

      if (!isReceiptNumberAdded) {
        const sanitizedLine1 = receiptNumber.substring(0, 25)
        paymentData += sanitizedLine1
        // paymentData += `${sanitizedLine1}\nПОЕНИ: 200` // TODO: Second line just for testing printers
        isReceiptNumberAdded = true
      }

      paymentData += `\t${paymentType}${value.toFixed(2)}`

      // 35h(53) CALCULATION OF A TOTAL
      fiscalReceiptCommands.push(
        [53, paymentData]
      )
    })

    if (defaultOptions.isRefund && !defaultOptions.isDavid) {
      // 56h(86) CLOSING A VOID RECEIPT
      fiscalReceiptCommands.push(
        [86, null]
      )
    } else {
      // 38h(56) CLOSING A FISCAL RECEIPT
      fiscalReceiptCommands.push(
        [56, null]
      )
    }

    const fiscalSaleData = {
      status: null,
      memory: null
    }

    await this.lock.acquire()

    try {
      await this.checkIfHasUnfinishedFiscalOperationAndAbortIt()

      for (const data of fiscalReceiptCommands) {
        const res = await this.serialWrapper.runCommand(data[0], data[1])
        // console.log('messages:', res.messages)
        // console.log('status codes:', res.responseStatusCodes)
        // console.log('data:', res.data)

        if (res.responseStatusCodes.includes(this.serialWrapper.getStatusCodes().get('GENERAL_ERROR'))) {
          throw new Error('General error')
        } else if (res.responseStatusCodes.includes(this.serialWrapper.getStatusCodes().get('SYNTAX_ERROR'))) {
          throw new Error('Syntax error')
        } else if (res.responseStatusCodes.includes(this.serialWrapper.getStatusCodes().get('COMMAND_CANNOT_BE_EXECUTED_IN_CURRENT_MODE'))) {
          throw new Error('Cannot be executed in current printer mode')
        }
      }

      const fiscalNumber = await this.getSaleFiscalNumber()
      fiscalSaleData.memory = fiscalNumber
      fiscalSaleData.status = 'ok'

      return fiscalSaleData
    } catch (error) {
      await this.playErrorBeep()
      await this.cancelFiscalOperation()
      throw error
    } finally {
      this.lock.release()
    }
  }

  async getSaleFiscalNumber () {
    const res = await this.serialWrapper.runCommand(48, '*')
    return res.data
  }

  async runDailyFinancialReport (closure = true) {
    const data = closure ? '0' : '2'
    return this.serialWrapper.runCommand(69, data)
  }

  async runShortFinancialReportByDate (start, end) {
    const data = end ? `${start},${end}` : start
    return this.serialWrapper.runCommand(79, data)
  }

  async runExtendedFinancialReportByDate (start, end) {
    const data = end ? `${start},${end}` : start
    return this.serialWrapper.runCommand(94, data)
  }

  async getDiagnosticInfo () {
    return this.serialWrapper.runCommand(71)
  }

  async getLastFiscalRecord () {
    return await this.serialWrapper.runCommand(64, '0')
  }

  async movePaper () {
    return await this.serialWrapper.runCommand(44, 1)
  }

  async cutPaper () {
    return await this.serialWrapper.runCommand(45)
  }

  async moneyInOut (amount) {
    return await this.serialWrapper.runCommand(70, amount)
  }

  async clearDisplay () {
    return await this.serialWrapper.runCommand(33)
  }

  async setDate (date) {
    return await this.serialWrapper.runCommand(61, `${date}`)
  }

  async getDate () {
    return await this.serialWrapper.runCommand(62)
  }

  async addTextLowerLineDisplay (text) {
    return await this.serialWrapper.runCommand(35, text.substring(0, 20))
  }

  async addTextUpperLineDisplay (text) {
    return await this.serialWrapper.runCommand(47, text.substring(0, 20))
  }

  getVatGroupBytes (vatGroup) {
    switch (vatGroup) {
      case 'MKVat0':
        return 'В'
      case 'MKVat18':
        return 'А'
      case 'MKVat5':
        return 'Б'
      case 'MKVat10':
        return 'Г'
      case 'Vat0':
        return 'C'
      case 'Vat18':
        return 'A'
      case 'Vat5':
        return 'B'
      case 'Vat10':
        return 'D'
      default:
        return '' // Empty string
    }
  }

  getPaymentTypeString (paymentType, isDavid = false) {
    switch (paymentType) {
      case 'card':
        return isDavid ? 'N' : 'D'
      case 'cheque':
        return isDavid ? 'B' : 'C'
      case 'credit':
        return isDavid ? 'C' : 'N'
      case 'voucher': // bonovi
        return 'D'
      default:
        return 'P' // cash
    }
  }

  getNoteFrequency (note) {
    const notesFreq = {
      c: 261,
      d: 294,
      e: 329,
      f: 349,
      g: 391,
      gS: 415,
      a: 440,
      aS: 455,
      b: 466,
      cH: 523,
      cSH: 554,
      dH: 587,
      dSH: 622,
      eH: 659,
      fH: 698,
      fSH: 740,
      gH: 784,
      gSH: 830,
      aH: 880
    }

    return notesFreq[note]
  }

  cancelFiscalOperation () {
    return this.serialWrapper.runCommand(60)
  }

  async playMusicSteps (musicSteps) {
    await this.lock.acquire()

    try {
      for (const musicData of musicSteps) {
        if (musicData[0] === 0 && musicData[1] === 0) {
          await new Promise(resolve => setTimeout(resolve, musicData[2]))
        } else {
          const data = `${musicData[0]},${musicData[1]}`
          const delay = parseInt(musicData[1]) + parseInt(musicData[2])

          await this.serialWrapper.runCommand(80, data)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
    } finally {
      this.lock.release()
    }
  }

  async playErrorBeep () {
    const musicSteps = [
      [this.getNoteFrequency('a'), '1500', '50']
    ]

    return this.playMusicSteps(musicSteps)
  }

  async playMusic () {
    // Star Wars Theme
    const musicSteps = [
      [this.getNoteFrequency('a'), '500', '30'],
      [this.getNoteFrequency('a'), '500', '30'],
      [this.getNoteFrequency('a'), '500', '30'],
      [this.getNoteFrequency('f'), '350', '30'],
      [this.getNoteFrequency('cH'), '150', '30'],
      [this.getNoteFrequency('a'), '500', '30'],
      [this.getNoteFrequency('f'), '350', '30'],
      [this.getNoteFrequency('cH'), '150', '30'],
      [this.getNoteFrequency('a'), '150', '30'],
      [0, 0, '150'], // end part one
      [this.getNoteFrequency('eH'), '500', '30'],
      [this.getNoteFrequency('eH'), '500', '30'],
      [this.getNoteFrequency('eH'), '500', '30'],
      [this.getNoteFrequency('fH'), '350', '30'],
      [this.getNoteFrequency('cH'), '150', '30'],
      [this.getNoteFrequency('gS'), '500', '30'],
      [this.getNoteFrequency('f'), '350', '30'],
      [this.getNoteFrequency('cH'), '150', '30'],
      [this.getNoteFrequency('a'), '650', '30'],
      [0, 0, '150'], // end part two
      [this.getNoteFrequency('aH'), '500', '30'],
      [this.getNoteFrequency('a'), '300', '30'],
      [this.getNoteFrequency('a'), '150', '30'],
      [this.getNoteFrequency('aH'), '400', '30'],
      [this.getNoteFrequency('gSH'), '200', '30'],
      [this.getNoteFrequency('gH'), '200', '30'],
      [this.getNoteFrequency('fSH'), '125', '30'],
      [this.getNoteFrequency('fH'), '125', '30'],
      [this.getNoteFrequency('fSH'), '250', '30'],
      [0, 0, '250'], // end part three
      [this.getNoteFrequency('aS'), '250', '30'],
      [this.getNoteFrequency('dSH'), '400', '30'],
      [this.getNoteFrequency('dH'), '200', '30'],
      [this.getNoteFrequency('cSH'), '200', '30'],
      [this.getNoteFrequency('cH'), '125', '30'],
      [this.getNoteFrequency('b'), '125', '30'],
      [this.getNoteFrequency('cH'), '250', '30'],
      [0, 0, '250'] // end part four
    ]

    return this.playMusicSteps(musicSteps)
  }

  filterUndefinedValues (obj) {
    const ret = {}
    Object.keys(obj)
      .filter((key) => obj[key] !== undefined)
      .forEach((key) => { ret[key] = obj[key] })
    return ret
  }
}

export default API
